import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { CloseConfirmationDropdown } from '../CloseConfirmationDropdown/CloseConfirmationDropdown';
import CustomButton from '../Button/CustomButton';

export interface IGenericModalWrapper {
    onBack: () => void;
    children?: React.ReactNode;
    header?: string;
    leadingText?: string;
    onClose: () => void;
    hasUnsavedChanges?: boolean;
    formData?: Record<string, any>;
    handleOnSubmit?: (...args: any) => void;
    disableButton?: boolean;
    loading?: boolean;
    hideGlobalButton?: boolean;
}

const FOOTER_BUTTON_HEIGHT = 60; // px
// const FOOTER_VERTICAL_PADDING = 24; // 12px top + 12px bottom (py:1.5 -> 1.5*8)
// const FOOTER_TOTAL_HEIGHT = FOOTER_BUTTON_HEIGHT + FOOTER_VERTICAL_PADDING; // 84px

const GenericModalWrapper: React.FC<IGenericModalWrapper> = ({
    onBack,
    children,
    header,
    leadingText,
    onClose,
    hasUnsavedChanges = true,
    formData,
    handleOnSubmit,
    disableButton,
    loading,
    hideGlobalButton = true,
}) => {
    const { themeStyle } = useThemeContext();
    const colors = theme[themeStyle];

    return (
        <Box sx={{ width: '100%', flexShrink: 0, minHeight: '500px', maxHeight: { xs: '100dvh' } }}>
            {/* Sticky Header */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    px: 1,
                    py: 0.5,
                    bgcolor: theme[themeStyle].secondaryBg,
                }}>
                <IconButton onClick={onBack} sx={{ color: colors.tag_white }}>
                    <ArrowBackIcon />
                </IconButton>

                <Typography
                    variant="h6"
                    sx={{
                        color: colors.tag_white,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'capitalize',
                    }}>
                    {header}
                </Typography>

                {/* Keep close button in the header row; no absolute positioning needed */}
                <CloseConfirmationDropdown
                    onClose={onClose}
                    hasUnsavedChanges={hasUnsavedChanges}
                    formData={formData!}
                    sx={{ cursor: 'pointer' }}
                />
            </Box>

            {leadingText && (
                <Typography
                    variant="h6"
                    sx={{
                        color: colors.tag_white,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 14,
                        mb: 1,
                    }}>
                    {leadingText}
                </Typography>
            )}

            {children}

            {/* Sticky Footer */}
            {!hideGlobalButton && (
                <Box
                    component="footer"
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 3,
                        px: 2,
                        py: 1.5,
                        // optional backdrop for glassy look:
                        // backdropFilter: 'saturate(180%) blur(10px)',
                        bgcolor: theme[themeStyle].secondaryBg,
                    }}>
                    <CustomButton
                        label="Save"
                        onClick={handleOnSubmit}
                        customStyle={{ width: '100%', height: `${FOOTER_BUTTON_HEIGHT}px` }}
                        disabled={disableButton}
                        loading={loading}
                    />
                </Box>
            )}
        </Box>
    );
};

export default GenericModalWrapper;
